import axios from "axios";

import { auth } from "../containers/firebase";

const VITE_CLOUD_FUNCTION_API_URL = import.meta.env.VITE_CLOUD_FUNCTION_API_URL || "";

const IMPERSONATION_STORAGE_KEY = "gp.impersonation.v1";
export const IMPERSONATION_STATE_CHANGED_EVENT = "gp:impersonation-state-changed";

const IMPERSONATION_START_ENDPOINT = "/impersonationStart";
const IMPERSONATION_STOP_ENDPOINT = "/app/v2/impersonation/stop";

const notifyImpersonationStateChanged = () => {
    if (typeof window === "undefined") {
        return;
    }

    window.dispatchEvent(new CustomEvent(IMPERSONATION_STATE_CHANGED_EVENT));
};

const buildEndpoint = (path) => {
    if (!VITE_CLOUD_FUNCTION_API_URL) {
        return null;
    }

    return `${VITE_CLOUD_FUNCTION_API_URL}${path}`;
};

const getResponsePayload = (response) => {
    return response?.data?.data ?? response?.data ?? {};
};

const getErrorMessage = (error, fallbackMessage) => {
    return error?.response?.data?.message || error?.message || fallbackMessage;
};

export const isImpersonationEnabled = () => {
    if (typeof window === "undefined") {
        return Boolean(import.meta.env.DEV);
    }

    const hostname = window.location?.hostname || "";
    const isLocalhost = ["localhost", "127.0.0.1", "::1"].includes(hostname);

    return Boolean(import.meta.env.DEV || isLocalhost);
};

export const getStoredImpersonationState = () => {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        const raw = window.localStorage.getItem(IMPERSONATION_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (_) {
        return null;
    }
};

export const persistImpersonationState = (state) => {
    if (typeof window === "undefined") {
        return;
    }

    window.localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(state));
    notifyImpersonationStateChanged();
};

export const clearImpersonationState = () => {
    if (typeof window === "undefined") {
        return;
    }

    window.localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    notifyImpersonationStateChanged();
};

const stopImpersonationWithToken = async ({ token, impersonationId, swallowErrors = false }) => {
    const url = buildEndpoint(IMPERSONATION_STOP_ENDPOINT);

    if (!url || !token) {
        if (swallowErrors) {
            return null;
        }

        throw new Error("Impersonation API is not configured.");
    }

    try {
        const response = await axios.post(
            url,
            {
                impersonationId: impersonationId || "",
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        return getResponsePayload(response);
    } catch (error) {
        if (swallowErrors) {
            return null;
        }

        throw new Error(getErrorMessage(error, "Failed to stop impersonation."));
    }
};

export const startImpersonation = async ({ email, reason }) => {
    if (!isImpersonationEnabled()) {
        throw new Error("Impersonation is disabled.");
    }

    const adminUser = auth.currentUser;

    if (!adminUser) {
        throw new Error("No active admin session.");
    }

    const normalizedEmail = String(email || "").trim();

    if (!normalizedEmail) {
        throw new Error("Email is required to start impersonation.");
    }

    const adminToken = await adminUser.getIdToken();
    const url = buildEndpoint(IMPERSONATION_START_ENDPOINT);

    if (!adminToken || !url) {
        throw new Error("Failed to start impersonation.");
    }

    let payload;

    try {
        const response = await axios.post(
            url,
            {
                email: normalizedEmail,
                ...(reason ? { reason: String(reason).trim() } : {}),
            },
            {
                headers: {
                    Authorization: `Bearer ${adminToken}`,
                },
            }
        );

        payload = getResponsePayload(response);
    } catch (error) {
        throw new Error(getErrorMessage(error, "Invalid impersonation payload from backend."));
    }

    const customToken = payload?.customToken;
    const impersonationId = payload?.impersonationId;

    if (!customToken || !impersonationId) {
        throw new Error("Invalid impersonation payload from backend.");
    }

    try {
        const credential = await auth.signInWithCustomToken(customToken);
        const impersonatedUser = credential?.user;

        if (!impersonatedUser) {
            throw new Error("Impersonated user is missing after custom token sign-in.");
        }

        const state = {
            impersonationId,
            impersonatedUid: impersonatedUser.uid,
            impersonatedEmail: normalizedEmail,
            impersonatorUid: adminUser.uid,
            impersonatorEmail: adminUser.email || "",
            startedAt: Date.now(),
        };

        persistImpersonationState(state);
        return state;
    } catch (error) {
        clearImpersonationState();
        await stopImpersonationWithToken({
            token: adminToken,
            impersonationId,
            swallowErrors: true,
        });

        throw new Error(getErrorMessage(error, "Failed to switch impersonation session."));
    }
};

export const stopImpersonation = async ({ signOut = true } = {}) => {
    if (!isImpersonationEnabled()) {
        throw new Error("Impersonation is disabled.");
    }

    const state = getStoredImpersonationState();
    let stopError = null;

    try {
        const currentToken = await auth.currentUser?.getIdToken();

        if (currentToken) {
            await stopImpersonationWithToken({
                token: currentToken,
                impersonationId: state?.impersonationId,
            });
        }
    } catch (error) {
        stopError = error;
    } finally {
        clearImpersonationState();
    }

    if (signOut) {
        await auth.signOut();
    }

    if (stopError) {
        throw stopError;
    }

    return true;
};

export const logoutCurrentSession = async () => {
    const impersonationState = getStoredImpersonationState();

    if (impersonationState?.impersonationId) {
        await stopImpersonation({ signOut: true });
        return true;
    }

    clearImpersonationState();
    await auth.signOut();
    return true;
};
